var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import { withAttachments } from 'utils/graphql';
const createInitialState = () => ({
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        update: false,
        remove: false,
    },
});
const mutateWithAttachments = withAttachments((options) => client.mutate(options));
export function createProductParameterSlice(config) {
    const fetchItems = createAsyncThunk(`${config.name}/fetch`, (_, { getState, rejectWithValue }) => __awaiter(this, void 0, void 0, function* () {
        const state = getState();
        const { pageInfo } = state.parameters[config.stateKey];
        try {
            const response = yield client.query({
                query: config.query,
                variables: {
                    first: 25,
                    after: pageInfo.endCursor,
                },
                fetchPolicy: 'network-only',
            });
            return config.selectConnection(response.data);
        }
        catch (error) {
            const apolloError = error;
            return rejectWithValue(apolloError.message);
        }
    }));
    const addItem = createAsyncThunk(`${config.name}/add`, ({ name, description, thumbnail }, { rejectWithValue }) => __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield mutateWithAttachments({
                mutation: config.addMutation,
                variables: { name, description, thumbnail },
                fetchPolicy: 'no-cache',
            });
            return config.selectAddedItem(response.data);
        }
        catch (error) {
            const apolloError = error;
            return rejectWithValue(apolloError.message);
        }
    }));
    const updateItem = createAsyncThunk(`${config.name}/update`, ({ id, name, description, thumbnail, }, { rejectWithValue }) => __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield mutateWithAttachments({
                mutation: config.updateMutation,
                variables: { [config.idVariableName]: id, name, description, thumbnail },
            });
            return config.selectUpdatedItem(response.data);
        }
        catch (error) {
            const apolloError = error;
            return rejectWithValue(apolloError.message);
        }
    }));
    const removeItem = createAsyncThunk(`${config.name}/remove`, (id, { rejectWithValue }) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield client.mutate({
                mutation: config.removeMutation,
                variables: { [config.idVariableName]: id },
            });
            return id;
        }
        catch (error) {
            const apolloError = error;
            return rejectWithValue(apolloError.message);
        }
    }));
    const slice = createSlice({
        name: config.name,
        initialState: createInitialState(),
        reducers: {
            reset: () => createInitialState(),
        },
        extraReducers: (builder) => {
            builder
                .addCase(fetchItems.pending, (state) => {
                state.status.fetch = 'loading';
                state.refetchFlags.fetch = false;
            })
                .addCase(fetchItems.fulfilled, (state, action) => {
                var _a;
                const newItems = action.payload.edges.map((edge) => edge.node);
                const uniqueItems = newItems.filter((newItem) => !state.items.some((existingItem) => existingItem.id === newItem.id));
                state.items = [...state.items, ...uniqueItems];
                state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
                state.status.fetch = 'succeeded';
            })
                .addCase(fetchItems.rejected, (state, action) => {
                state.status.fetch = 'failed';
                state.errors.fetch = action.payload || action.error.message || null;
            })
                .addCase(addItem.pending, (state) => {
                state.status.add = 'loading';
            })
                .addCase(addItem.fulfilled, (state, action) => {
                state.items = [...state.items, action.payload].sort((a, b) => a.name.localeCompare(b.name));
                state.status.add = 'succeeded';
            })
                .addCase(addItem.rejected, (state, action) => {
                state.status.add = 'failed';
                state.errors.add = action.payload || action.error.message || null;
            })
                .addCase(updateItem.pending, (state) => {
                state.status.update = 'loading';
            })
                .addCase(updateItem.fulfilled, (state, action) => {
                const itemIndex = state.items.findIndex((item) => item.id === action.payload.id);
                if (itemIndex !== -1) {
                    state.items[itemIndex] = action.payload;
                }
                state.items = [...state.items].sort((a, b) => a.name.localeCompare(b.name));
                state.status.update = 'succeeded';
            })
                .addCase(updateItem.rejected, (state, action) => {
                state.status.update = 'failed';
                state.errors.update = action.payload || action.error.message || null;
            })
                .addCase(removeItem.pending, (state) => {
                state.status.remove = 'loading';
            })
                .addCase(removeItem.fulfilled, (state, action) => {
                state.items = state.items.filter((item) => item.id !== action.payload);
                state.status.remove = 'succeeded';
            })
                .addCase(removeItem.rejected, (state, action) => {
                state.status.remove = 'failed';
                state.errors.remove = action.payload || action.error.message || null;
            });
        },
    });
    const selectState = (state) => state.parameters[config.stateKey];
    return {
        reducer: slice.reducer,
        reset: slice.actions.reset,
        fetchItems,
        addItem,
        updateItem,
        removeItem,
        selectItems: (state) => selectState(state).items,
        selectItemById: (state, id) => selectState(state).items.find((item) => item.id === id),
        selectPageInfo: (state) => selectState(state).pageInfo,
        selectStatus: (state) => selectState(state).status,
        selectErrors: (state) => selectState(state).errors,
        selectRefetchFlags: (state) => selectState(state).refetchFlags,
    };
}
